/**
 * +field: content registries for world sessions. Fields and the town: the base content with the
 * one character compiled from the profile (gear, gems, talents, +specs: the chosen specialisation).
 * A levelled dungeon: the hero plus Group Finder companions compiled from starter gear at the hero's
 * level (progression compileCompanion). A failed compile falls back to the base classes (logged,
 * never fatal).
 */
import type { ClassId, ContentRegistry } from '@keystone/sim';
import { type CompiledCharacter, type Profile, buildPartyRegistry, compileCompanion, compileProfileCharacter, groupFinderParty, heroRole, partyClassDef } from '@keystone/progression';

export interface WorldRegistry {
  registry: ContentRegistry;
  error: string | null;
}

export function heroLevelOf(profile: Profile): number {
  const c = profile.character;
  return (c && profile.characters[c.classId]?.hero.level) || 1;
}

/**
 * The hero compiled from the profile. `trialSpecId` (+specs, the Advancement Trial's "try it")
 * compiles a copy of the profile with that spec chosen: a local preview, never saved.
 */
function compileHero(profile: Profile, base: ContentRegistry, classId: ClassId, trialSpecId: string | null = null): CompiledCharacter {
  const def = base.bundle.classes.find((k) => k.id === classId);
  if (!def) throw new Error(`class ${classId} is not in the content bundle`);
  if (!trialSpecId) return compileProfileCharacter(profile, def);
  const p = structuredClone(profile);
  const ch = p.characters[classId];
  if (ch) ch.hero.specId = trialSpecId;
  return compileProfileCharacter(p, def);
}

/** The registry for a field / town session (just the hero compiled; `trialSpecId` for a 30 s spec preview). */
export function fieldRegistry(profile: Profile, base: ContentRegistry, trialSpecId: string | null = null): WorldRegistry {
  const c = profile.character;
  if (!c) return { registry: base, error: 'no character' };
  try {
    return { registry: buildPartyRegistry(base.bundle, [compileHero(profile, base, c.classId, trialSpecId)]), error: null };
  } catch (err) {
    return { registry: base, error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * A Group Finder party in slot order (tank, healer, dps, dps) by ROLE (+specs): the hero takes its
 * role's slot (its spec's role once chosen); strangers at 10+ are random specs of their slot's role,
 * registered under spec keys ("warrior@ironclad"). Deterministic in `seed`.
 */
export function dungeonParty(profile: Profile, seed = 1): ClassId[] {
  const c = profile.character;
  if (!c) return [];
  return groupFinderParty(c.classId, heroRole(profile), heroLevelOf(profile), seed).map((s) => s.classId);
}

/** The registry for a levelled dungeon: the hero from the profile, companions at the hero's level. */
export function dungeonRegistry(profile: Profile, base: ContentRegistry, party: readonly ClassId[]): WorldRegistry {
  const c = profile.character;
  if (!c) return { registry: base, error: 'no character' };
  const level = heroLevelOf(profile);
  try {
    const compiled = party.map((id) => {
      if (id === c.classId) return compileHero(profile, base, id);
      const def = partyClassDef(id) ?? base.bundle.classes.find((k) => k.id === id);
      if (!def) throw new Error(`class ${id} is not in the content bundle`);
      return compileCompanion(def, level);
    });
    return { registry: buildPartyRegistry(base.bundle, compiled), error: null };
  } catch (err) {
    return { registry: base, error: err instanceof Error ? err.message : String(err) };
  }
}
